import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Tooltip,
    useTheme,
    TextField,
    Alert,
    Checkbox,
    IconButton,
    ButtonGroup,
    Divider,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormHelperText,
    Autocomplete
} from "@mui/material";
import { useAtom } from "jotai";
import {
    bookingDetailsAtom,
    getSelectedLocationAtom,
    getSelectedServiceAtom,
    setDetailAtom,
    setUpcomingSessionsDetailAtom,
    showOnlyWhoIsAvailableDialogAtom,
    whoIsAvailableSlotsAtom,
    whoIsAvailableSlotsNearestDateSortedAtom,
    whoIsAvailableTimezoneAtom
} from "../atoms/booking";
import { ClientAddressInput } from "../components/ClientAddressInput";
import {
    CLIENT_LOCATION_ID,
    DEFAULT_BUFFER_TIME_MINS,
    DEFAULT_RRULE,
    SLOT_TYPES
} from "../utils/Constants";
import React, { useEffect, useRef, useState } from "react";
import LoadingButton from "@mui/lab/LoadingButton";
import { Box, ThemeProvider } from "@mui/system";
import {
    allActiveLocationsAtom,
    allActiveServicesAtom,
    allActiveSkillsAtom,
    companyAtom,
    getProviderServicesByProviderIdAtom
} from "../atoms/atoms";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers";
import { enUS } from "date-fns/locale";
import CustomTimePicker from "../user/components/CustomTimePicker";
import { addDays, parseISO } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { postApiWithStatusCode } from "../modules/AmplifyServices";
import {
    formatDateInfoAttributes,
    getFullTimeZoneNameForTimeZone,
    getTimezoneIdByGeoLoc
} from "../modules/TimeService";
import { userPageSnackbarAtom } from "../atoms/usersTable";
import {
    daysMonFirst,
    rfcRRuleAtom,
    weekdaysAtom
} from "../atoms/recurringSettings";
import { DialogTheme } from "../styles/DialogTheme";
import LeafSvg from "../images/LeafSvg";
import WarningTriangleIcon from "../images/WarningTriangleIcon.svg";
import { SuggestedSlotsTheme } from "../styles/SuggestedSlotsTheme";
import InfoIcon from "@mui/icons-material/Info";
import { isCountryImperial, jsonParse, truncateName } from "../utils";

export const WhoIsAvailableDialog = ({
    open,
    onClose,
    onDateTimeChange,
    handleProviderChange,
    handleClientAutoFocus
}) => {
    const theme = useTheme();
    const [showWhoIsAvailableSlotsForm, setShowWhoIsAvailableSlotsForm] =
        useState(false);
    const [, setWhoIsAvailableSlots] = useAtom(whoIsAvailableSlotsAtom);
    const [, setWhoIsAvailableTimezone] = useAtom(whoIsAvailableTimezoneAtom);

    const handleClose = () => {
        setShowWhoIsAvailableSlotsForm(false);
        setWhoIsAvailableSlots([]);
        setWhoIsAvailableTimezone(null);
        onClose();
    };

    return (
        <ThemeProvider theme={DialogTheme(theme)}>
            <Dialog open={open} onClose={handleClose}>
                {showWhoIsAvailableSlotsForm ? (
                    <WhoIsAvailableSlotsForm
                        onClose={handleClose}
                        setShowWhoIsAvailableSlotsForm={
                            setShowWhoIsAvailableSlotsForm
                        }
                        onDateTimeChange={onDateTimeChange}
                        handleProviderChange={handleProviderChange}
                        handleClientAutoFocus={handleClientAutoFocus}
                    />
                ) : (
                    <WhoIsAvailableForm
                        onClose={handleClose}
                        setShowWhoIsAvailableSlotsForm={
                            setShowWhoIsAvailableSlotsForm
                        }
                    />
                )}
            </Dialog>
        </ThemeProvider>
    );
};

const WhoIsAvailableForm = ({ onClose, setShowWhoIsAvailableSlotsForm }) => {
    const theme = useTheme();
    const [serviceSelectorAutoFocus, setServiceSelectorAutoFocus] =
        useState(false);
    const [addressSelectorAutoFocus, setAddressSelectorAutoFocus] =
        useState(false);
    const serviceSelectorRef = useRef(null);
    const addressSelectorRef = useRef(null);
    const [company] = useAtom(companyAtom);
    const [, setUserPageSnackbar] = useAtom(userPageSnackbarAtom);
    const [
        {
            selectedLocationId,
            selectedServiceId,
            isRepeating,
            providerLocationIds,
            sessions,
            selectedPackage,
            providerId
        }
    ] = useAtom(bookingDetailsAtom);
    const [activeLocations] = useAtom(allActiveLocationsAtom);
    const locations = [
        {
            id: "customerLocation",
            locationname: "Client address"
        },
        ...activeLocations
    ];
    const [, setDetail] = useAtom(setDetailAtom);
    const [, setWhoIsAvailableSlots] = useAtom(whoIsAvailableSlotsAtom);
    const [, setWhoIsAvailableTimezone] = useAtom(whoIsAvailableTimezoneAtom);
    const [allActiveSkills] = useAtom(allActiveSkillsAtom);
    const skills = allActiveSkills.map((skill) => ({
        id: skill.id,
        name: skill.name
    }));
    const firstSessionDateTime = new Date(sessions[0].dateTime);
    const rangeDefaultStart = firstSessionDateTime.setHours(8, 0, 0, 0);
    const rangeDefaultEnd = addDays(firstSessionDateTime, 7).setHours(
        18,
        0,
        0,
        0
    );
    const [weekdays, setWeekdays] = useAtom(weekdaysAtom);
    const [rrule] = useAtom(rfcRRuleAtom);
    const autocompleteService = { current: null };
    const [selectedLocation] = useAtom(getSelectedLocationAtom);
    const [selectedService] = useAtom(getSelectedServiceAtom);
    const [services] = useAtom(allActiveServicesAtom);
    const providerServices = useAtom(getProviderServicesByProviderIdAtom)[0](
        providerId
    );
    const [selectedSkills, setSelectedSkills] = useState([]);
    const [, setUpcomingSessionsDetail] = useAtom(
        setUpcomingSessionsDetailAtom
    );
    const [openDate, setOpenDate] = useState(false);
    const [openEndDate, setOpenEndDate] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [addressError, setAddressError] = useState(false);
    const [selectedDate, setSelectedDate] = useState(rangeDefaultStart);
    const [selectedTime, setSelectedTime] = useState(rangeDefaultStart);
    const [selectedEndDate, setSelectedEndDate] = useState(rangeDefaultEnd);
    const [selectedEndTime, setSelectedEndTime] = useState(rangeDefaultEnd);

    useEffect(() => {
        if (serviceSelectorAutoFocus && serviceSelectorRef.current) {
            serviceSelectorRef.current.focus();

            //reset the focus state after focusing
            setServiceSelectorAutoFocus(false);
        }
    }, [serviceSelectorAutoFocus]);

    useEffect(() => {
        if (addressSelectorAutoFocus && addressSelectorRef.current) {
            addressSelectorRef.current.focus();

            //reset the focus state after focusing
            setAddressSelectorAutoFocus(false);
        }
    }, [addressSelectorAutoFocus]);

    useEffect(() => {
        if (selectedLocationId === CLIENT_LOCATION_ID && !selectedLocation) {
            setAddressSelectorAutoFocus(true);
        }
    }, []);

    const daysStyleFunction = (val) => {
        return {
            width: 36,
            height: 36,
            borderRadius: "50%",
            backgroundColor: val ? "primary.main" : "white",
            borderStyle: "solid",
            borderWidth: "1px",
            borderColor: val ? "primary.main" : "rgb(0,0,0,0.23)",
            minWidth: 0,
            color: val ? "white" : "primary.main",
            "&:hover": {
                backgroundColor: "primary.main",
                borderColor: "primary.main",
                color: "white"
            },
            "&.Mui-disabled": {
                borderColor: theme.palette.text.disabled
            }
        };
    };

    const handleWeekdayToggle = (dayIndex) => {
        setWeekdays((prev) => {
            //if trying to remove the last day, return the current state unchanged
            if (prev.length === 1 && prev.includes(dayIndex)) {
                return prev;
            }

            return prev.includes(dayIndex)
                ? prev.filter((d) => d !== dayIndex)
                : [...prev, dayIndex];
        });
        //setUpcomingSessionsDetail(null);
    };

    const isDisabled =
        !selectedDate ||
        !selectedEndDate ||
        !selectedTime ||
        !selectedEndTime ||
        !selectedLocation ||
        !selectedService;

    const handleDateChange = (date) => {
        if (date) {
            setSelectedDate(date);

            const newDateTime = new Date(date);
            const timeDate = new Date(selectedTime);
            newDateTime.setHours(timeDate.getHours());
            newDateTime.setMinutes(timeDate.getMinutes());
            setSelectedTime(newDateTime);

            handleEndDateChange(addDays(date, 7));
        }
    };

    const handleTimeChange = (time) => {
        if (time) {
            const timeObj = new Date(time);
            setSelectedTime(timeObj);
            // Update date to match the selected time
            const newDateTime = new Date(selectedDate);
            newDateTime.setHours(timeObj.getHours());
            newDateTime.setMinutes(timeObj.getMinutes());
            setSelectedDate(newDateTime);
        }
    };

    const handleEndDateChange = (date) => {
        if (date) {
            const newDateTime = new Date(date);
            const timeDate = new Date(selectedEndTime);
            newDateTime.setHours(timeDate.getHours());
            newDateTime.setMinutes(timeDate.getMinutes());
            setSelectedEndDate(newDateTime);
            setSelectedEndTime(newDateTime);
        }
    };

    const handleEndTimeChange = (time) => {
        if (time) {
            const timeObj = new Date(time);
            setSelectedEndTime(timeObj);
            // Update date to match the selected time
            const newDateTime = new Date(selectedEndDate);
            newDateTime.setHours(timeObj.getHours());
            newDateTime.setMinutes(timeObj.getMinutes());
            setSelectedEndDate(newDateTime);
        }
    };

    const handleWhoIsAvailableRequest = async () => {
        setIsLoading(true);
        try {
            const locationTimeZone =
                selectedLocationId === CLIENT_LOCATION_ID
                    ? await getTimezoneIdByGeoLoc({
                          lat: selectedLocation.latitude,
                          lng: selectedLocation.longitude
                      })
                    : selectedLocation?.timezone;

            const { dtstamp_str: startDateTimeIso } = formatDateInfoAttributes(
                selectedDate,
                selectedService?.minutes || null,
                locationTimeZone
            );

            const { dtstamp_str: endDateTimeIso } = formatDateInfoAttributes(
                selectedEndDate,
                selectedService?.minutes || null,
                locationTimeZone
            );

            const {
                body: { timeSlots, timezone },
                statusCode
            } = await postApiWithStatusCode(
                "MB-rest-api",
                "/v1/mb/search/slots",
                {
                    body: {
                        companyId: company.id,
                        targetGeoLocation: [
                            selectedLocation.latitude,
                            selectedLocation.longitude
                        ],
                        isCompanyLocation:
                            selectedLocation.id !== CLIENT_LOCATION_ID,
                        companyLocationId:
                            selectedLocation.id !== CLIENT_LOCATION_ID
                                ? selectedLocation.id
                                : null,
                        targetServiceId: selectedService.id,
                        startDate: startDateTimeIso,
                        endDate: endDateTimeIso,
                        timezone: locationTimeZone,
                        daysOfWeek:
                            weekdays?.length > 0
                                ? weekdays
                                : [0, 1, 2, 3, 4, 5, 6],
                        skills:
                            selectedSkills?.length > 0 ? selectedSkills : [],
                        recurring: isRepeating,
                        rrule: rrule ?? DEFAULT_RRULE,
                        package: selectedPackage ? true : false,
                        distanceUnit:
                            selectedLocation?.countrycode3166alpha2?.toUpperCase() ===
                                "US" ||
                            selectedLocation?.countrycode3166alpha2?.toUpperCase() ===
                                "GB"
                                ? "mi"
                                : "km",
                        disableOpenSlots: true
                    }
                }
            );

            if (statusCode.toString().startsWith("2") && timeSlots.length > 0) {
                setWhoIsAvailableSlots(timeSlots);
                setWhoIsAvailableTimezone(timezone);
                setShowWhoIsAvailableSlotsForm(true);
            } else if (
                statusCode.toString().startsWith("2") &&
                timeSlots.length === 0
            ) {
                setWhoIsAvailableSlots([]);
                setUserPageSnackbar({
                    open: true,
                    message: "No slots found",
                    severity: "error"
                });
            } else {
                setUserPageSnackbar({
                    open: true,
                    message: "Error fetching slots",
                    severity: "error"
                });
            }

            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
            setUserPageSnackbar({
                open: true,
                message: "Error fetching slots",
                severity: "error"
            });
        }
    };

    return (
        <>
            <DialogTitle sx={{ paddingBottom: "32px" }}>
                Dispatcher Co-pilot
            </DialogTitle>
            <DialogContent sx={{ paddingBottom: "2px" }}>
                <Grid container spacing={2.25}>
                    <Grid item xs={12} sx={{ paddingTop: "0px !important" }}>
                        <Typography>
                            Use our Smart Clusters tool to find optimized time
                            slot options and best available providers
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sx={{ paddingTop: "32px !important" }}>
                        <FormControl fullWidth>
                            <InputLabel id="location-select-label" size="small">
                                Location
                            </InputLabel>
                            <Select
                                fullWidth
                                label="Location"
                                labelId="location-select-label"
                                name="location"
                                value={selectedLocationId}
                                disabled={activeLocations?.length === 0}
                                onChange={({
                                    target: { value: locationId }
                                }) => {
                                    setDetail({
                                        selectedLocationId: locationId
                                    });
                                    if (
                                        !selectedService &&
                                        locationId !== CLIENT_LOCATION_ID
                                    ) {
                                        setServiceSelectorAutoFocus(true);
                                    }
                                }}
                            >
                                <Divider
                                    sx={{
                                        paddingTop: "8px",
                                        borderColor: "white"
                                    }}
                                />
                                <MenuItem
                                    key={CLIENT_LOCATION_ID}
                                    value={CLIENT_LOCATION_ID}
                                >
                                    {locations[0].locationname}
                                </MenuItem>
                                <Divider />
                                {locations
                                    .slice(1)
                                    .map(({ id, locationname }) => (
                                        <MenuItem
                                            key={id}
                                            value={id}
                                            sx={{
                                                color:
                                                    selectedService &&
                                                    !providerLocationIds?.includes(
                                                        "CL-" + id
                                                    ) &&
                                                    id !== CLIENT_LOCATION_ID
                                                        ? theme.palette
                                                              .helperText.main
                                                        : ""
                                            }}
                                        >
                                            <Tooltip
                                                title={locationname}
                                                disableHoverListener={
                                                    locationname.length < 50
                                                }
                                                placement="right"
                                            >
                                                <Box
                                                    sx={{
                                                        display: "-webkit-box",
                                                        WebkitLineClamp: 1,
                                                        WebkitBoxOrient:
                                                            "vertical",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis"
                                                    }}
                                                >
                                                    <Typography
                                                        variant="inherit"
                                                        component="span"
                                                    >
                                                        {locationname}
                                                    </Typography>
                                                </Box>
                                            </Tooltip>
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        {selectedLocationId === CLIENT_LOCATION_ID ? (
                            <ClientAddressInput
                                setAddress={(value) => {
                                    setDetail({
                                        clientAddress: {
                                            addressoneline:
                                                value.addressOneLine,
                                            country: value.country,
                                            countrycode3166alpha2:
                                                value.countryShortName,
                                            id: CLIENT_LOCATION_ID,
                                            latitude: value.coords.lat,
                                            longitude: value.coords.lng,
                                            locationname: value.addressOneLine,
                                            state: value.prov,
                                            city: value.city,
                                            postalcode: value.postalCode,
                                            isRemoteLocation: true,
                                            isVirtual: false
                                        }
                                    });
                                    if (!selectedService) {
                                        setServiceSelectorAutoFocus(true);
                                    }
                                }}
                                focus={addressSelectorAutoFocus}
                                inputRef={addressSelectorRef}
                                formWidth={"100%"}
                                formMargin={"0px"}
                                formMarginLeft={"0px"}
                                label={"Address"}
                                addressOneLine={
                                    selectedLocation?.addressoneline || ""
                                }
                                /* inputDisplay={clientAddressOneLine || null} */
                                autocompleteService={autocompleteService}
                                smallSize={true}
                                error={addressError}
                                setAddressError={setAddressError}
                                setPid={(value) => {
                                    setDetail({
                                        placeId: value
                                    });
                                }}
                                onClear={() =>
                                    setDetail({
                                        clientAddress: null
                                    })
                                }
                            />
                        ) : (
                            <TextField
                                value={selectedLocation?.addressoneline || ""}
                                disabled
                                label="Address"
                            />
                        )}
                    </Grid>
                    <Grid item xs={12}>
                        <Autocomplete
                            id="select-service"
                            disableClearable
                            size="small"
                            options={services}
                            focus={serviceSelectorAutoFocus}
                            openOnFocus={true}
                            getOptionLabel={(option) =>
                                option.name ? option.name : ""
                            }
                            isOptionEqualToValue={(option, value) => {
                                return option.name === value.name;
                            }}
                            variant="outlined"
                            value={selectedService || null}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Service"
                                    InputProps={{
                                        ...params.InputProps
                                    }}
                                    InputLabelProps={{
                                        ...params.InputLabelProps
                                    }}
                                    variant="outlined"
                                    fullWidth
                                    inputRef={serviceSelectorRef}
                                />
                            )}
                            renderOption={(props, option) => (
                                <li {...props}>
                                    <Tooltip
                                        title={option.name}
                                        disableHoverListener={
                                            option.name.length < 50
                                        }
                                        placement="right"
                                    >
                                        <Box
                                            sx={{
                                                display: "-webkit-box",
                                                WebkitLineClamp: 1,
                                                WebkitBoxOrient: "vertical",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis"
                                            }}
                                        >
                                            <Typography
                                                variant="inherit"
                                                component="span"
                                            >
                                                {`${
                                                    !jsonParse(
                                                        option.locations,
                                                        []
                                                    ).includes(
                                                        selectedLocationId
                                                    ) &&
                                                    selectedLocationId !==
                                                        CLIENT_LOCATION_ID
                                                        ? "*"
                                                        : ""
                                                }${option.name}`}
                                            </Typography>
                                        </Box>
                                    </Tooltip>
                                </li>
                            )}
                            onChange={(e, value) => {
                                setDetail({
                                    selectedServiceId: value.id
                                });
                            }}
                        />
                        {selectedService &&
                            !jsonParse(selectedService.locations, []).includes(
                                selectedLocationId
                            ) &&
                            selectedLocationId !== CLIENT_LOCATION_ID && (
                                <Grid xs={12}>
                                    <FormHelperText
                                        error
                                        sx={{ lineHeight: "14px" }}
                                    >
                                        {
                                            "* This service is not available at the selected location"
                                        }
                                    </FormHelperText>
                                </Grid>
                            )}
                    </Grid>
                    <Grid item xs={12} sx={{ paddingTop: "0px !important" }}>
                        <LocalizationProvider
                            dateAdapter={AdapterDateFns}
                            adapterLocale={{
                                ...enUS,
                                options: { ...enUS.options, weekStartsOn: 1 }
                            }}
                        >
                            <Grid container spacing={2.25}>
                                <Grid item xs={12} sm={6}>
                                    <DatePicker
                                        label="7-day search between"
                                        value={selectedDate}
                                        onChange={handleDateChange}
                                        open={openDate}
                                        onOpen={() => setOpenDate(true)}
                                        onClose={() => {
                                            setOpenDate(false);
                                        }}
                                        inputFormat="MMM dd, yyyy"
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                fullWidth
                                                size="small"
                                                inputProps={{
                                                    ...params.inputProps,
                                                    readOnly: true
                                                }}
                                                sx={{
                                                    "& .MuiInputAdornment-root":
                                                        {
                                                            display: "none"
                                                        }
                                                }}
                                                onClick={(e) => {
                                                    setOpenDate(true);
                                                }}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <DatePicker
                                        label="And"
                                        value={selectedEndDate}
                                        onChange={handleEndDateChange}
                                        open={openEndDate}
                                        onOpen={() => setOpenEndDate(true)}
                                        onClose={() => {
                                            setOpenEndDate(false);
                                        }}
                                        disabled
                                        minDate={selectedDate}
                                        maxDate={addDays(selectedDate, 7)}
                                        inputFormat="MMM dd, yyyy"
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                fullWidth
                                                size="small"
                                                inputProps={{
                                                    ...params.inputProps,
                                                    readOnly: true
                                                }}
                                                disabled
                                                sx={{
                                                    "& .MuiInputAdornment-root":
                                                        {
                                                            display: "none"
                                                        }
                                                }}
                                            />
                                        )}
                                    />
                                </Grid>
                            </Grid>
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} sx={{ paddingTop: "0px !important" }}>
                        <LocalizationProvider
                            dateAdapter={AdapterDateFns}
                            adapterLocale={enUS}
                        >
                            <Grid container spacing={2.25}>
                                <Grid item xs={12} sm={6}>
                                    <CustomTimePicker
                                        id="start-time"
                                        type="startTime"
                                        label="Time range between"
                                        value={selectedTime}
                                        onChange={handleTimeChange}
                                        outlinedInputSx={{
                                            padding: "7.5px 14px",
                                            color: theme.palette.text.main
                                        }}
                                        step={15}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <CustomTimePicker
                                        id="end-time"
                                        type="endTime"
                                        label="And"
                                        value={selectedEndTime}
                                        onChange={handleEndTimeChange}
                                        outlinedInputSx={{
                                            padding: "7.5px 14px",
                                            color: theme.palette.text.main
                                        }}
                                        step={15}
                                    />
                                </Grid>
                            </Grid>
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12}>
                        <Box
                            display="flex"
                            flexDirection="row"
                            gap={1}
                            sx={{ alignItems: "center" }}
                        >
                            <ButtonGroup
                                sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: "10px",
                                    width: "100%",
                                    justifyContent: "space-between"
                                }}
                            >
                                {daysMonFirst.map((day) => (
                                    <IconButton
                                        key={day.id}
                                        onClick={() =>
                                            handleWeekdayToggle(day.id)
                                        }
                                        sx={daysStyleFunction(
                                            weekdays.includes(day.id)
                                        )}
                                    >
                                        <Box
                                            sx={{
                                                fontSize: "14px",
                                                fontWeight: "500",
                                                fontFamily: "roboto"
                                            }}
                                        >
                                            {day.title}
                                        </Box>
                                    </IconButton>
                                ))}
                            </ButtonGroup>
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <InputLabel id="skills-label" size="small">
                                Skills
                            </InputLabel>

                            <Select
                                labelId="skills-label"
                                id="skills"
                                name="skills"
                                label="Skills"
                                multiple
                                sx={{
                                    "& fieldset": {
                                        "& legend": {
                                            width: "33px"
                                        }
                                    }
                                }}
                                value={selectedSkills}
                                onChange={(e) => {
                                    setSelectedSkills(e.target.value);
                                }}
                                renderValue={() =>
                                    selectedSkills.length ===
                                    allActiveSkills.length
                                        ? "All skills"
                                        : allActiveSkills
                                              .filter((skill) =>
                                                  selectedSkills.includes(
                                                      skill.id
                                                  )
                                              )
                                              .map((skill) => skill.name)
                                              .join(", ")
                                }
                            >
                                {skills.map((skill) => (
                                    <MenuItem value={skill.id}>
                                        <Checkbox
                                            checked={selectedSkills.includes(
                                                skill.id
                                            )}
                                        />
                                        {skill.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{ paddingTop: "34px", gap: "10px" }}>
                <Button variant="outlined" onClick={onClose}>
                    Cancel
                </Button>
                <LoadingButton
                    variant="contained"
                    disabled={isDisabled}
                    loading={isLoading}
                    onClick={() => {
                        handleWhoIsAvailableRequest();
                    }}
                >
                    Search
                </LoadingButton>
            </DialogActions>
        </>
    );
};

const WhoIsAvailableSlotsForm = ({
    onClose,
    setShowWhoIsAvailableSlotsForm,
    onDateTimeChange,
    handleProviderChange,
    handleClientAutoFocus
}) => {
    const [{ isRepeating, selectedPackage, sessions, provider }] =
        useAtom(bookingDetailsAtom);
    const [company] = useAtom(companyAtom);
    const SHORTEST_TRAVEL_TIME_FILTER = "shortestTravelTime";
    const NEAREST_DATE_FILTER = "nearestDate";
    const theme = useTheme();
    const [showPartialAvailabilityDialog, setShowPartialAvailabilityDialog] =
        useState(false);
    const [whoIsAvailableSlots, setWhoIsAvailableSlots] = useAtom(
        whoIsAvailableSlotsAtom
    );
    const [, setWhoIsAvailableTimezone] = useAtom(whoIsAvailableTimezoneAtom);
    const [whoIsAvailableTimezone] = useAtom(whoIsAvailableTimezoneAtom);
    const [, setShowOnlyWhoIsAvailableDialog] = useAtom(
        showOnlyWhoIsAvailableDialogAtom
    );
    const [whoIsAvailableSlotsNearestDateSorted] = useAtom(
        whoIsAvailableSlotsNearestDateSortedAtom
    );
    const [selectedSlotIndex, setSelectedSlotIndex] = useState(null);
    const [filterOption, setFilterOption] = useState(NEAREST_DATE_FILTER);
    const [selectedLocation] = useAtom(getSelectedLocationAtom);

    const displayedSlots =
        filterOption === NEAREST_DATE_FILTER
            ? whoIsAvailableSlotsNearestDateSorted
            : whoIsAvailableSlots;
    const distanceUnit =
        selectedLocation?.countrycode3166alpha2?.toUpperCase() === "US" ||
        selectedLocation?.countrycode3166alpha2?.toUpperCase() === "GB"
            ? "mi"
            : "km";

    const handleReturnToFilters = () => {
        setWhoIsAvailableSlots([]);
        setWhoIsAvailableTimezone(null);
        setShowWhoIsAvailableSlotsForm(false);
    };

    const getSlotType = () => {
        if (displayedSlots[selectedSlotIndex]?.travelType === "ON-EMPTY-DAY") {
            return SLOT_TYPES.SUGGESTED_SLOT_EMPTY_DAY;
        } else if (
            displayedSlots[selectedSlotIndex]?.travelTimeMins <= 5 ||
            displayedSlots[selectedSlotIndex]?.travelType?.startsWith(
                "SAME-LOCATION"
            )
        ) {
            return SLOT_TYPES.SUGGESTED_SLOT_OPTIMIZED;
        } else {
            return SLOT_TYPES.SUGGESTED_SLOT;
        }
    };

    return (
        <>
            <DialogTitle sx={{ paddingBottom: "32px" }}>
                Suggested times
            </DialogTitle>
            <DialogContent
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "27px",
                    padding: "0px"
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "27px",
                        [theme.breakpoints.up("md")]: {
                            padding: "0px 36px"
                        }
                    }}
                >
                    {whoIsAvailableTimezone && (
                        <Alert
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: "10px 0px",
                                border: `1px solid ${theme.palette.info.main}`,
                                marginBottom: "0px",
                                backgroundColor: theme.palette.info.lightest,
                                textAlign: "center"
                            }}
                            severity="info"
                            icon={<InfoIcon />}
                        >
                            <Typography
                                color={theme.palette.info.main}
                                fontWeight={500}
                                display={"inline"}
                            >
                                Times shown in{" "}
                                {getFullTimeZoneNameForTimeZone(
                                    new Date(),
                                    whoIsAvailableTimezone
                                ) || ""}
                            </Typography>
                        </Alert>
                    )}

                    <Grid item xs={12}>
                        <Button
                            variant="text"
                            color="info"
                            onClick={handleReturnToFilters}
                            sx={{
                                "&:hover": {
                                    backgroundColor: "transparent !important"
                                }
                            }}
                            disableRipple
                        >
                            <ArrowBackIcon
                                sx={{
                                    height: "18px",
                                    transform: "translateX(-6px)"
                                }}
                                color="primary"
                            />
                            <Typography
                                variant="button"
                                color="primary"
                                sx={{
                                    textTransform: "none",
                                    lineHeight: "16px"
                                }}
                            >
                                {`Back to filters`}
                            </Typography>
                        </Button>
                    </Grid>

                    {sessions.length &&
                        sessions[0].locationTzDateTimeIso &&
                        provider && (
                            <Box>
                                <Typography>Current Selection:</Typography>
                                <Typography
                                    sx={{
                                        whiteSpace: "pre-wrap",
                                        fontWeight: "700"
                                    }}
                                >
                                    {`${provider.firstname} ${provider.lastname}   >>>   ${formatInTimeZone(
                                        parseISO(
                                            sessions[0].locationTzDateTimeIso
                                        ),
                                        sessions[0].locationTzDateTimeIso.match(
                                            /[+-]\d{2}:\d{2}$/
                                        )[0],
                                        "E, MMM dd – h:mm aa"
                                    )}`}
                                </Typography>
                            </Box>
                        )}

                    {/* Sort by Filter Options */}
                    <Box sx={{ height: "14px" }}>
                        <FormControl component="fieldset">
                            <RadioGroup
                                row
                                value={filterOption}
                                onChange={(e) =>
                                    setFilterOption(e.target.value)
                                }
                            >
                                <FormControlLabel
                                    value={NEAREST_DATE_FILTER}
                                    control={<Radio size="small" />}
                                    label="Sort by nearest date"
                                    sx={{
                                        "& .MuiFormControlLabel-label": {
                                            fontSize: "14px"
                                        }
                                    }}
                                />
                                <FormControlLabel
                                    value={SHORTEST_TRAVEL_TIME_FILTER}
                                    control={<Radio size="small" />}
                                    label="Sort by least travel time"
                                    sx={{
                                        "& .MuiFormControlLabel-label": {
                                            fontSize: "14px"
                                        }
                                    }}
                                />
                            </RadioGroup>
                        </FormControl>
                    </Box>
                </Box>

                {/* This Box makes the Grid scrollable */}
                <Box
                    sx={{
                        overflowY: "auto",
                        padding: "8px 0px 0px 14px",
                        [theme.breakpoints.down("md")]: {
                            padding: "8px 0px 0px 0px"
                        },
                        marginTop: "-8px",
                        display: "flex",
                        justifyContent: "center"
                    }}
                >
                    <Grid
                        container
                        spacing={2.25}
                        sx={{ gap: "18px", margin: "0px", maxWidth: "378px" }}
                    >
                        {displayedSlots.map((slot, index) => (
                            <ThemeProvider
                                theme={SuggestedSlotsTheme({
                                    palette: theme.palette
                                })}
                            >
                                <Box
                                    display={"flex"}
                                    alignItems={"center"}
                                    width={"100%"}
                                    position={"relative"}
                                    justifyContent={"center"}
                                >
                                    <Button
                                        variant={
                                            selectedSlotIndex === index
                                                ? "contained"
                                                : "outlined"
                                        }
                                        onClick={() =>
                                            setSelectedSlotIndex(index)
                                        }
                                        sx={{
                                            display: "flex",
                                            justifyContent: "center",
                                            flexDirection: "column",
                                            width: "100%",
                                            height: "42px",
                                            borderRadius: "40px",
                                            "&:hover": {
                                                backgroundColor:
                                                    selectedSlotIndex === index
                                                        ? theme.palette.primary
                                                              .main
                                                        : theme.palette.primary
                                                              .medium,
                                                cursor: "pointer"
                                            }
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                lineHeight: "16px",
                                                whiteSpace: "pre-wrap"
                                            }}
                                        >
                                            {`${truncateName(slot.providerFirstName, slot.providerLastName)}   >>>   ${formatInTimeZone(
                                                parseISO(
                                                    isRepeating
                                                        ? slot.slots[0].timeSlotDateAndTime.replace(
                                                              /\d{2}:\d{2}(?=:\d{2}[+-]\d{2}:\d{2}$)/,
                                                              slot.timePattern
                                                          )
                                                        : slot.timeSlotDateAndTime
                                                ),
                                                isRepeating
                                                    ? slot.slots[0].timeSlotDateAndTime
                                                          .replace(
                                                              /\d{2}:\d{2}(?=:\d{2}[+-]\d{2}:\d{2}$)/,
                                                              slot.timePattern
                                                          )
                                                          .match(
                                                              /[+-]\d{2}:\d{2}$/
                                                          )[0]
                                                    : slot.timeSlotDateAndTime.match(
                                                          /[+-]\d{2}:\d{2}$/
                                                      )[0],
                                                "E, MMM dd – h:mm aa"
                                            )}`}
                                        </Typography>

                                        <Typography
                                            variant="helperText"
                                            sx={{
                                                fontSize: "12px",
                                                fontStyle: "italic",
                                                lineHeight: "14px",
                                                color:
                                                    selectedSlotIndex === index
                                                        ? "white"
                                                        : ""
                                            }}
                                        >
                                            {slot.travelType === "ON-EMPTY-DAY"
                                                ? `Empty day, first booking`
                                                : slot.travelType ===
                                                    "SAME-LOCATION-AS-PREVIOUS"
                                                  ? `Same location as previous booking`
                                                  : slot.travelType ===
                                                      "SAME-LOCATION-AS-NEXT"
                                                    ? `Same location as next booking`
                                                    : `${slot.travelTimeMins - DEFAULT_BUFFER_TIME_MINS} mins travel ${
                                                          slot.travelType ===
                                                          "FROM-PREVIOUS-BOOKING"
                                                              ? "from previous booking"
                                                              : "to next booking"
                                                      } (${(slot?.travelDistance || 0).toFixed(1)} ${distanceUnit}) + ${DEFAULT_BUFFER_TIME_MINS} mins buffer`}
                                        </Typography>
                                        <Tooltip
                                            placement="right"
                                            style={{
                                                position: "absolute",
                                                top: "-2px",
                                                right: "0px",
                                                transform:
                                                    "translate(24%, -24%)"
                                            }}
                                            componentsProps={{
                                                tooltip: {
                                                    sx: {
                                                        whiteSpace: "pre-line",
                                                        width: "175px"
                                                    }
                                                }
                                            }}
                                            arrow
                                            title={
                                                isRepeating &&
                                                slot.consistency < 1 ? (
                                                    <Typography
                                                        sx={{
                                                            lineHeight: "16px"
                                                        }}
                                                    >
                                                        {selectedPackage ? (
                                                            <div>
                                                                {`This time is only available for ${(slot?.slots?.length * slot?.consistency).toFixed(0)} out of ${slot?.slots?.length} appointments in this package`}
                                                            </div>
                                                        ) : (
                                                            `This time is not available for every appointment in the recurring set`
                                                        )}
                                                    </Typography>
                                                ) : slot.travelTimeMins <= 5 &&
                                                  slot.travelType !==
                                                      "ON-EMPTY-DAY" ? (
                                                    <Typography>
                                                        Less than 5 minutes
                                                        travel time
                                                    </Typography>
                                                ) : (
                                                    ""
                                                )
                                            }
                                        >
                                            {isRepeating &&
                                            slot.consistency < 1 ? (
                                                <span>
                                                    <img
                                                        src={
                                                            WarningTriangleIcon
                                                        }
                                                        alt="Warning Triangle Icon"
                                                        style={{
                                                            height: "22px",
                                                            width: "22px",
                                                            position:
                                                                "absolute",
                                                            top: "-2px",
                                                            right: "0px",
                                                            transform:
                                                                "translate(24%, -24%)"
                                                        }}
                                                    />
                                                </span>
                                            ) : (slot.travelTimeMins <= 5 &&
                                                  slot.travelType !==
                                                      "ON-EMPTY-DAY") ||
                                              slot.travelType.startsWith(
                                                  "SAME-LOCATION"
                                              ) ? (
                                                <span>
                                                    <LeafSvg />
                                                </span>
                                            ) : null}
                                        </Tooltip>
                                    </Button>
                                </Box>
                            </ThemeProvider>
                        ))}
                    </Grid>
                </Box>
            </DialogContent>
            <DialogActions
                sx={{
                    paddingTop: "34px",
                    gap: "10px",
                    [theme.breakpoints.down("md")]: {
                        padding: "18px"
                    }
                }}
            >
                <Button variant="outlined" onClick={onClose}>
                    Cancel
                </Button>
                <LoadingButton
                    variant="contained"
                    disabled={selectedSlotIndex === null}
                    onClick={() => {
                        if (displayedSlots[selectedSlotIndex].consistency < 1) {
                            setShowPartialAvailabilityDialog(true);
                        } else {
                            handleClientAutoFocus();
                            handleProviderChange(
                                displayedSlots[selectedSlotIndex].providerId
                            );
                            onDateTimeChange(
                                isRepeating
                                    ? displayedSlots[selectedSlotIndex].slots
                                    : new Date(
                                          displayedSlots[
                                              selectedSlotIndex
                                          ].timeSlotDateAndTime
                                      ),
                                getSlotType()
                            );
                            setShowOnlyWhoIsAvailableDialog(false);
                        }
                    }}
                >
                    Book{" "}
                    {selectedSlotIndex !== null
                        ? `${displayedSlots[selectedSlotIndex].providerFirstName}`
                        : "a time"}
                </LoadingButton>
            </DialogActions>
            {showPartialAvailabilityDialog && (
                <PartialAvailabilityDialog
                    onClose={() => setShowPartialAvailabilityDialog(false)}
                    selectedSlot={displayedSlots[selectedSlotIndex]}
                    onDateTimeChange={onDateTimeChange}
                    handleProviderChange={handleProviderChange}
                    handleClientAutoFocus={handleClientAutoFocus}
                    showPartialAvailabilityDialog={
                        showPartialAvailabilityDialog
                    }
                />
            )}
        </>
    );
};

const PartialAvailabilityDialog = ({
    onClose,
    selectedSlot,
    onDateTimeChange,
    handleProviderChange,
    showPartialAvailabilityDialog,
    handleClientAutoFocus
}) => {
    const [{ selectedPackage }] = useAtom(bookingDetailsAtom);
    const [, setShowOnlyWhoIsAvailableDialog] = useAtom(
        showOnlyWhoIsAvailableDialogAtom
    );
    const alternativeSlots = selectedSlot.slots.filter((slot) => {
        if (!selectedSlot.timePattern) return false;

        const timePart = slot.timeSlotDateAndTime.match(/T(\d{2}:\d{2})/)[1];
        return timePart !== selectedSlot.timePattern;
    });

    const getPartialAvailabilityMessage = (alternativeSlots) => {
        if (selectedPackage) {
            return (
                <>
                    <Typography>
                        {selectedSlot.providerFirstName} can only do the
                        selected time for{" "}
                        <span style={{ fontWeight: "bold" }}>
                            {" "}
                            {selectedSlot?.slots?.length -
                                alternativeSlots?.length}{" "}
                            out of {selectedSlot?.slots?.length} appointments in
                            this package{" "}
                        </span>{" "}
                        – for the {alternativeSlots?.length} remaining{" "}
                        {alternativeSlots?.length === 1 ? "date" : "dates"}{" "}
                        listed below, the system will find the closes possible
                        alternative and you can delete or override those
                        manually if necessary:
                    </Typography>
                    <Grid container spacing={2.25}>
                        {alternativeSlots.map((slot, index) => (
                            <Grid item key={index} xs={12}>
                                {" "}
                                <ul
                                    style={{
                                        paddingLeft: 36,
                                        margin: 0
                                    }}
                                >
                                    <li>
                                        <Typography>
                                            {formatInTimeZone(
                                                parseISO(
                                                    slot.timeSlotDateAndTime
                                                ),
                                                slot.timeSlotDateAndTime.match(
                                                    /[+-]\d{2}:\d{2}$/
                                                )[0],
                                                "E, MMM dd, yyyy"
                                            )}
                                        </Typography>
                                    </li>
                                </ul>
                            </Grid>
                        ))}
                    </Grid>
                </>
            );
        } else {
            return (
                <>
                    <Typography>
                        {selectedSlot.providerFirstName} can only do the
                        selected time for{" "}
                        <span style={{ fontWeight: "bold" }}>
                            {" "}
                            {selectedSlot?.slots?.length -
                                alternativeSlots?.length}{" "}
                            out of {selectedSlot?.slots?.length} appointments in
                            this recurring set{" "}
                        </span>{" "}
                        – the system will find the closest possible alternative
                        for the remaining {alternativeSlots?.length}{" "}
                        appointments and you can delete or override those
                        manually if necessary
                    </Typography>
                    {selectedSlot?.slots?.length >= 10 && (
                        <Typography sx={{ paddingTop: "18px" }}>
                            <span style={{ fontWeight: "bold" }}>WARNING:</span>{" "}
                            there may be other conflicts after the 10th
                            appointment, but our system cannot look that far
                            ahead
                        </Typography>
                    )}
                </>
            );
        }
    };

    const getSlotType = () => {
        if (selectedSlot?.travelType === "ON-EMPTY-DAY") {
            return SLOT_TYPES.SUGGESTED_SLOT_EMPTY_DAY;
        } else if (
            selectedSlot?.travelTimeMins <= 5 ||
            selectedSlot?.travelType.startsWith("SAME-LOCATION")
        ) {
            return SLOT_TYPES.SUGGESTED_SLOT_OPTIMIZED;
        } else {
            return SLOT_TYPES.SUGGESTED_SLOT;
        }
    };

    return (
        <Dialog open={showPartialAvailabilityDialog} onClose={onClose}>
            <DialogTitle sx={{ paddingBottom: "32px" }}>
                Partial availability
            </DialogTitle>
            <DialogContent sx={{ display: "flex", flexDirection: "column" }}>
                {getPartialAvailabilityMessage(alternativeSlots)}
                <Grid container spacing={2.25}>
                    <Grid item xs={12}>
                        <Typography>
                            Click “Cancel” to select a different time slot or
                            provider
                        </Typography>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{ paddingTop: "34px", gap: "10px" }}>
                <Button variant="outlined" onClick={onClose}>
                    Cancel
                </Button>
                <LoadingButton
                    variant="contained"
                    onClick={() => {
                        handleClientAutoFocus();
                        handleProviderChange(selectedSlot.providerId);
                        onDateTimeChange(selectedSlot.slots, getSlotType());
                        setShowOnlyWhoIsAvailableDialog(false);
                    }}
                >
                    Continue
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};
